/**
 * Offload Responsibilities — Freedom Engine Assessment & Plan Builder
 *
 * Full integration of @roi-pros/freedom-engine — the suite-standard
 * assessment → score → plan → pricing flow. Lets a workspace owner
 * map out which BeHeard activities to delegate or automate, what the
 * effort looks like, and what plan + pricing fits.
 *
 * Flow:
 *   1. Assessment   — answer 1-N scale questions across pillars
 *   2. Score        — Freedom Score + pillar breakdown + initiative picker
 *   3. Plan         — phased rollout + pricing + timeline
 *
 * No placeholder content. No mailto: shortcuts. Real engine, real flow.
 *
 * Route: /offload-responsibilities  (wired via beheardShellAdapter.config.offloadRoute)
 */

import { useCallback, useMemo, useState } from 'react';
import {
  buildPlan,
  calculateFreedomScore,
  calculatePricing,
  formatClientOutput,
  loadFrameworkDataset,
  type AssessmentAnswers,
  type ClientOutput,
  type FrameworkDataset,
  type FreedomScoreResult,
  type Initiative,
  type Plan,
  type PricingResult,
} from '@roi-pros/freedom-engine';
import frameworkJson from '@roi-pros/freedom-engine/data/freedom-framework.json';

// Load framework once at module scope (pure, no side effects)
const dataset: FrameworkDataset = loadFrameworkDataset(frameworkJson);

type Step = 'assess' | 'score' | 'plan';

export function OffloadResponsibilitiesPage() {
  const [step, setStep] = useState<Step>('assess');
  const [answers, setAnswers] = useState<AssessmentAnswers>({});
  const [scoreResult, setScoreResult] = useState<FreedomScoreResult | null>(null);
  const [selectedInitiativeIds, setSelectedInitiativeIds] = useState<string[]>([]);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [pricing, setPricing] = useState<PricingResult | null>(null);
  const [clientOutput, setClientOutput] = useState<ClientOutput | null>(null);

  // Flatten questions with pillar/category context
  const allQuestions = useMemo(
    () =>
      dataset.pillars.flatMap((pillar) =>
        pillar.categories.flatMap((cat) =>
          cat.questions.map((q) => ({
            ...q,
            pillarName: pillar.name,
            categoryName: cat.name,
          })),
        ),
      ),
    [],
  );

  const isAssessmentComplete = allQuestions.every((q) => answers[q.id] != null);

  const handleAnswer = useCallback((questionId: string, value: number) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  const handleScore = useCallback(() => {
    const result = calculateFreedomScore(answers, dataset);
    setScoreResult(result);
    // Pre-select all initiatives so the user can deselect rather than start empty
    setSelectedInitiativeIds(dataset.initiatives.map((i) => i.id));
    setStep('score');
  }, [answers]);

  const handleBuildPlan = useCallback(() => {
    const builtPlan = buildPlan({ selectedInitiativeIds }, dataset);
    setPlan(builtPlan);
    const pricingResult = calculatePricing(builtPlan, builtPlan.capacityTierId, dataset);
    setPricing(pricingResult);
    setClientOutput(formatClientOutput(builtPlan, pricingResult));
    setStep('plan');
  }, [selectedInitiativeIds]);

  const handleStartOver = useCallback(() => {
    setStep('assess');
    setAnswers({});
    setScoreResult(null);
    setSelectedInitiativeIds([]);
    setPlan(null);
    setPricing(null);
    setClientOutput(null);
  }, []);

  const toggleInitiative = useCallback((id: string) => {
    setSelectedInitiativeIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Offload Responsibilities</h1>
      <p className="text-sm text-gray-500 mb-8">
        Assess your operations, identify what to delegate or automate, and build
        a phased plan to free up your time for high-value work.
      </p>

      <Stepper step={step} />

      {step === 'assess' && (
        <AssessmentStep
          questions={allQuestions}
          answers={answers}
          onAnswer={handleAnswer}
          onSubmit={handleScore}
          isComplete={isAssessmentComplete}
        />
      )}

      {step === 'score' && scoreResult && (
        <ScoreStep
          result={scoreResult}
          initiatives={dataset.initiatives}
          selectedIds={selectedInitiativeIds}
          onToggleInitiative={toggleInitiative}
          onBuildPlan={handleBuildPlan}
          onBack={() => setStep('assess')}
        />
      )}

      {step === 'plan' && plan && pricing && clientOutput && (
        <PlanStep
          plan={plan}
          pricing={pricing}
          clientOutput={clientOutput}
          onStartOver={handleStartOver}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Stepper
// ──────────────────────────────────────────────────────────────

const STEPS: Array<{ key: Step; label: string }> = [
  { key: 'assess', label: 'Assessment' },
  { key: 'score', label: 'Results' },
  { key: 'plan', label: 'Plan' },
];

function Stepper({ step }: { step: Step }) {
  const activeIndex = STEPS.findIndex((s) => s.key === step);
  return (
    <div className="flex items-center gap-2 mb-8">
      {STEPS.map((s, i) => (
        <div key={s.key} className="flex items-center gap-2">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step === s.key
                ? 'bg-primary-600 text-white'
                : activeIndex > i
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-400'
            }`}
          >
            {i + 1}
          </div>
          <span
            className={`text-sm ${
              step === s.key ? 'font-semibold text-gray-900' : 'text-gray-400'
            }`}
          >
            {s.label}
          </span>
          {i < STEPS.length - 1 && <div className="w-8 h-px bg-gray-200" />}
        </div>
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Assessment Step
// ──────────────────────────────────────────────────────────────

interface QuestionWithContext {
  id: string;
  text: string;
  scale: number;
  pillarName: string;
  categoryName: string;
}

function AssessmentStep({
  questions,
  answers,
  onAnswer,
  onSubmit,
  isComplete,
}: {
  questions: QuestionWithContext[];
  answers: AssessmentAnswers;
  onAnswer: (id: string, value: number) => void;
  onSubmit: () => void;
  isComplete: boolean;
}) {
  const grouped = useMemo(() => {
    const map = new Map<string, QuestionWithContext[]>();
    for (const q of questions) {
      const group = map.get(q.pillarName) ?? [];
      group.push(q);
      map.set(q.pillarName, group);
    }
    return Array.from(map.entries());
  }, [questions]);

  const answered = questions.filter((q) => answers[q.id] != null).length;
  const total = questions.length;

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">
            {answered} of {total} answered
          </span>
          <span className="text-xs text-gray-400">1 = strongly disagree · {questions[0]?.scale ?? 5} = strongly agree</span>
        </div>
        <div className="mt-2 w-full bg-gray-100 rounded-full h-2">
          <div
            className="bg-primary-500 h-2 rounded-full transition-all"
            style={{ width: total > 0 ? `${(answered / total) * 100}%` : '0%' }}
          />
        </div>
      </div>

      {grouped.map(([pillarName, qs]) => (
        <div key={pillarName} className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{pillarName}</h2>
          <div className="space-y-5">
            {qs.map((q) => (
              <div key={q.id}>
                <p className="text-sm text-gray-700 mb-2">
                  <span className="text-xs text-gray-400 mr-1">{q.categoryName}</span>
                  {q.text}
                </p>
                <div className="flex gap-2">
                  {Array.from({ length: q.scale }, (_, i) => i + 1).map((val) => (
                    <button
                      key={val}
                      onClick={() => onAnswer(q.id, val)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium border transition-colors ${
                        answers[q.id] === val
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-primary-300'
                      }`}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <button
        onClick={onSubmit}
        disabled={!isComplete}
        className="w-full py-3 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        Calculate Freedom Score
      </button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Score Step
// ──────────────────────────────────────────────────────────────

function ScoreStep({
  result,
  initiatives,
  selectedIds,
  onToggleInitiative,
  onBuildPlan,
  onBack,
}: {
  result: FreedomScoreResult;
  initiatives: readonly Initiative[];
  selectedIds: string[];
  onToggleInitiative: (id: string) => void;
  onBuildPlan: () => void;
  onBack: () => void;
}) {
  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
        <p className="text-sm text-gray-500 mb-1">Your Freedom Score</p>
        <p className="text-5xl font-bold text-primary-600">{Math.round(result.overallScore)}</p>
        <p className="mt-2 text-lg font-medium text-gray-900">{result.band.name}</p>
        <p className="text-sm text-gray-500 mt-1">{result.band.description}</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Pillar Breakdown</h2>
        <div className="space-y-3">
          {result.pillarScores.map((ps) => (
            <div key={ps.pillarId}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-700">{ps.pillarName}</span>
                <span className="font-medium text-gray-900">{Math.round(ps.score)}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div
                  className="bg-primary-500 h-2 rounded-full transition-all"
                  style={{ width: `${ps.score}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Recommended Initiatives</h2>
        <p className="text-sm text-gray-500 mb-4">
          Select which initiatives to include in your plan.
        </p>
        <div className="space-y-2">
          {initiatives.map((init) => (
            <label
              key={init.id}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                selectedIds.includes(init.id)
                  ? 'border-primary-300 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(init.id)}
                onChange={() => onToggleInitiative(init.id)}
                className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{init.name}</p>
                <p className="text-xs text-gray-500 mt-0.5">{init.description}</p>
                <div className="flex gap-3 mt-1">
                  <span className="text-xs text-gray-400">{init.pillar}</span>
                  <span className="text-xs text-gray-400">{init.effortHours}h</span>
                  <ComplexityBadge complexity={init.complexity} />
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="flex-1 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          onClick={onBuildPlan}
          disabled={selectedIds.length === 0}
          className="flex-1 py-3 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Build Plan ({selectedIds.length} initiatives)
        </button>
      </div>
    </div>
  );
}

function ComplexityBadge({ complexity }: { complexity: 'low' | 'medium' | 'high' }) {
  const tone =
    complexity === 'low'
      ? 'text-green-600'
      : complexity === 'medium'
        ? 'text-amber-600'
        : 'text-rose-600';
  return <span className={`text-xs ${tone}`}>{complexity}</span>;
}

// ──────────────────────────────────────────────────────────────
// Plan Step
// ──────────────────────────────────────────────────────────────

function PlanStep({
  plan,
  pricing,
  clientOutput,
  onStartOver,
}: {
  plan: Plan;
  pricing: PricingResult;
  clientOutput: ClientOutput;
  onStartOver: () => void;
}) {
  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">{clientOutput.title}</h2>
        <p className="text-sm text-gray-600">{clientOutput.overallSummary}</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <MetricCard
          label="Total Investment"
          value={`${pricing.currency} ${pricing.total.toLocaleString()}`}
        />
        <MetricCard label="Initiatives" value={plan.initiatives.length.toString()} />
        <MetricCard label="Phases" value={plan.phases.length.toString()} />
        <MetricCard label="Timeline" value={clientOutput.estimatedTimeline} />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Phase Breakdown</h2>
        <div className="space-y-4">
          {clientOutput.phases.map((phase) => (
            <div key={phase.phase} className="border border-gray-100 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-semibold text-gray-900">Phase {phase.phase}</span>
                <span className="text-xs text-gray-400">{phase.totalEffortHours}h</span>
              </div>
              <ul className="space-y-1">
                {phase.initiativeNames.map((name) => (
                  <li key={name} className="text-sm text-gray-600 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0" />
                    {name}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {pricing.appliedBundles.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <p className="text-sm font-medium text-green-800 mb-1">Bundle Savings Applied</p>
          {pricing.appliedBundles.map((b) => (
            <p key={b.bundleId} className="text-sm text-green-700">
              {b.bundleName}: {b.discountPercent}% off ({pricing.currency}{' '}
              {b.discountAmount.toLocaleString()} saved)
            </p>
          ))}
        </div>
      )}

      <button
        onClick={onStartOver}
        className="w-full py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
      >
        Start New Assessment
      </button>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-lg font-bold text-gray-900 mt-1">{value}</p>
    </div>
  );
}
